import { createNativeStackNavigator, NativeStackScreenProps } from '@react-navigation/native-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { useTranslation } from 'react-i18next';
import { useDripsyTheme } from 'dripsy';
import * as React from 'react';

import { UserEarnActionListResponseDataItem } from '@utx/utx-client';
import { LayoutContext } from 'contexts/LayoutContext';
import { Icon } from '@/design-system/components/icons/Icon';
import { TextSmall } from '@/design-system/components/typography';
import { DiscoverScreen } from './screens/discover/Discover.screen.web';
import { Portfolio } from './screens/portfolio/Portfolio.screen.web';
import { SubscribeNavigator } from './screens/subscription/subscribe.navigator';
import { ActivityHistoryDetails } from './screens/ActivityHistory/tabs/ActivityHistoryDetails.modal.web';
import { ActivityHistory } from './screens/ActivityHistory/ActivityHistory.screen';
import { AccountSelectorNativeModal } from '@/design-system/components/accountSelector/AccountSelectorNativeModal';
import { BottomSheetFilter, BottomSheetFilterProps } from './screens/bottomSheetFilter/BottomSheetFilter';
import { BottomSheetDatePicker, BottomSheetDatePickerProps } from './screens/bottomSheetDatePicker/BottomSheetDatePicker';
import { SubscriptionDetailsNavigator } from './screens/subscriptionDetails/subscriptionDetails.navigator';
import { EarnContextProvider } from './contexts/useEarnContext';

export enum EquivalentCurrency {
  HKD = 'HKD',
  USD = 'USD'
}

export enum EarnStackRoutes {
  Root = 'earn',
  Main = 'earn.main',
  Subscribe = 'earn.subscribe',
  Redeem = 'earn.redeem',
  ActivityHistoryDetails = 'earn.activityHistoryDetails',
  SubscriptionDetails = 'earn.subscriptionDetails',
  AutoRenewal = 'earn.autoRenewal',
  AccountSelect = 'earn.accountSelect',
  BottomSheetFilter = 'earn.buttomSheetFilter',
  BottomSheetDatePicker = 'earn.buttomSheetDatePicker'
}

export enum EarnTabRoutes {
  Discover = 'earn.discover',
  Portfolio = 'earn.portfolio',
  History = 'earn.history'
}

export type EarnStackParamList = {
  [EarnStackRoutes.Main]: undefined;
  [EarnStackRoutes.Subscribe]: {
    offerId: string;
  };
  [EarnStackRoutes.Redeem]: {
    assetSubscribedId: string;
    onUpdate: () => void;
  };
  [EarnStackRoutes.ActivityHistoryDetails]: {
    subscription: UserEarnActionListResponseDataItem;
  };
  [EarnStackRoutes.SubscriptionDetails]: {
    subscription: UserEarnActionListResponseDataItem;
    onUpdate: () => void;
    refreshPortfolio: () => void;
  };
  [EarnStackRoutes.AutoRenewal]: {
    subscription: UserEarnActionListResponseDataItem;
  };
  [EarnStackRoutes.AccountSelect]: {
    asset?: string;
    value?: string;
    disabledForwardBrowser?: boolean;
    onSelect: (walletId: string) => void;
  };
  [EarnStackRoutes.BottomSheetFilter]: BottomSheetFilterProps;
  [EarnStackRoutes.BottomSheetDatePicker]: BottomSheetDatePickerProps;
};

export type EarnTabParamList = {
  [EarnTabRoutes.Discover]: undefined;
  [EarnTabRoutes.Portfolio]: undefined;
  [EarnTabRoutes.History]: undefined;
};

export type EarnStackScreenProps<Screen extends keyof EarnStackParamList> = NativeStackScreenProps<EarnStackParamList, Screen>;

const EarnStack = createNativeStackNavigator<EarnStackParamList>();
const EarnTab = createBottomTabNavigator<EarnTabParamList>();

export function EarnNavigator(): JSX.Element {
  return (
    <EarnContextProvider>
      <EarnStack.Navigator
        screenOptions={{
          headerShown: false
        }}
        initialRouteName={EarnStackRoutes.Main}>
        {/* <EarnStack.Screen name={EarnStackRoutes.Main} component={EarnMain} /> */}
        <EarnStack.Screen name={EarnStackRoutes.Main} component={EarnTabNavigator} />
        {/* Subscription */}
        <EarnStack.Screen name={EarnStackRoutes.Subscribe} component={SubscribeNavigator} options={{ presentation: 'transparentModal' }} />
        {/* Activity History Details */}
        <EarnStack.Screen
          name={EarnStackRoutes.ActivityHistoryDetails}
          component={ActivityHistoryDetails}
          options={{ presentation: 'transparentModal' }}
        />

        {/* Subscription Details */}
        <EarnStack.Screen
          name={EarnStackRoutes.SubscriptionDetails}
          component={SubscriptionDetailsNavigator}
          options={{ presentation: 'transparentModal' }}
        />

        {/* Account Select */}
        <EarnStack.Screen
          name={EarnStackRoutes.AccountSelect}
          component={AccountSelectorNativeModal}
          options={{ presentation: 'transparentModal' }}
        />

        {/* Bottom Sheet Filter */}
        <EarnStack.Screen
          name={EarnStackRoutes.BottomSheetFilter}
          component={BottomSheetFilter}
          options={{ presentation: 'transparentModal' }}
        />

        {/* Bottom Sheet Date Picker */}
        <EarnStack.Screen
          name={EarnStackRoutes.BottomSheetDatePicker}
          component={BottomSheetDatePicker}
          options={{ presentation: 'transparentModal' }}
        />
      </EarnStack.Navigator>
    </EarnContextProvider>
  );
}

export function EarnTabNavigator(): JSX.Element {
  const { t } = useTranslation('earn');
  const layout = React.useContext(LayoutContext);
  const props = layout.isMobile ? {} : { tabBar: () => null };
  const { theme } = useDripsyTheme();

  const tabBarOptions = {
    paddingY: '$1',
    tabBarActiveTintColor: '#000000',
    tabBarInactiveTintColor: '#999999'
  };

  return (
    <EarnTab.Navigator
      screenOptions={{
        headerShown: false,
        unmountOnBlur: true,
        tabBarStyle: {
          height: 60,
          paddingTop: 8,
          paddingBottom: 8,
          shadowOffset: {
            width: 0,
            height: -10
          },
          shadowOpacity: 0.1,
          shadowRadius: 10,
          elevation: 0,
          backgroundColor: '$tabBarBackgroundColor',
          borderTopWidth: 0
        },
        tabBarItemStyle: {
          flexDirection: 'column'
        }
      }}
      sceneContainerStyle={{ backgroundColor: (theme.colors?.$backgroundSecondary as string) || 'transparent' }}
      detachInactiveScreens={true}
      initialRouteName={EarnTabRoutes.Discover}
      {...props}>
      <EarnTab.Screen
        name={EarnTabRoutes.Discover}
        component={DiscoverScreen}
        options={{
          ...tabBarOptions,
          tabBarLabel: ({ focused }) => (
            <TextSmall bold={focused} sx={{ mt: '$1', color: focused ? '$navbarTextActive' : '$navbarTextInactive' }}>
              {t('earn-invest::glossary.discover')}
            </TextSmall>
          ),
          tabBarIcon: ({ focused }) => <Icon name={focused ? 'earnNavDiscoverActive' : 'earnNavDiscoverInactive'} height={28} width={28} />
        }}
      />
      <EarnTab.Screen
        name={EarnTabRoutes.Portfolio}
        component={Portfolio}
        options={{
          ...tabBarOptions,
          tabBarLabel: ({ focused }) => (
            <TextSmall bold={focused} sx={{ mt: '$1', color: focused ? '$navbarTextActive' : '$navbarTextInactive' }}>
              {t('earn-invest::glossary.portfolio')}
            </TextSmall>
          ),
          tabBarIcon: ({ focused }) => (
            <Icon name={focused ? 'earnNavPortfolioActive' : 'earnNavPortfolioInactive'} height={28} width={28} />
          )
        }}
      />
      <EarnTab.Screen
        name={EarnTabRoutes.History}
        component={ActivityHistory}
        options={{
          ...tabBarOptions,
          tabBarLabel: ({ focused }) => (
            <TextSmall bold={focused} sx={{ mt: '$1', color: focused ? '$navbarTextActive' : '$navbarTextInactive' }}>
              {t('earn-invest::glossary.history')}
            </TextSmall>
          ),
          tabBarIcon: ({ focused }) => <Icon name={focused ? 'earnNavHistoryActive' : 'earnNavHistoryInactive'} height={28} width={28} />
        }}
      />
    </EarnTab.Navigator>
  );
}
